import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, ArrowRight } from 'lucide-react';
import { transferAPI, walletAPI } from '../services/api';
import { CRYPTO_NAMES, CRYPTO_SYMBOLS, NETWORKS } from '../utils/constants';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const Transfer = () => {
  const [balances, setBalances] = useState([]);
  const [transferData, setTransferData] = useState({
    crypto: 'BTC',
    amount: '',
    to_address: '',
    network: 'Bitcoin'
  });
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    loadBalances();
  }, []);

  const loadBalances = async () => {
    try {
      const response = await walletAPI.getBalances();
      setBalances(response.data.filter(b => b.crypto !== 'AED'));
    } catch (error) {
      toast.error('Failed to load balances');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setTransferring(true);

    try {
      await transferAPI.transferCrypto(transferData);
      toast.success(`Transferred ${transferData.amount} ${transferData.crypto}`);
      setTransferData({ crypto: 'BTC', amount: '', to_address: '', network: 'Bitcoin' });
      loadBalances();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const selectedBalance = balances.find(b => b.crypto === transferData.crypto);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-primary font-bold text-white mb-2">TRANSFER</h1>
          <p className="text-gray-400 font-secondary mb-8">Send crypto to another wallet</p>

          <div className="glass-card p-8 rounded-2xl">
            <form onSubmit={handleTransfer} className="space-y-6">
              {/* Select Crypto */}
              <div>
                <Label className="text-gray-300 mb-2 block">Cryptocurrency</Label>
                <Select value={transferData.crypto} onValueChange={(value) => {
                  setTransferData({ ...transferData, crypto: value, network: NETWORKS[value][0] });
                }}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="transfer-crypto-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A051E] border-white/10">
                    {Object.keys(NETWORKS).map(crypto => (
                      <SelectItem key={crypto} value={crypto} className="text-white hover:bg-white/10">
                        {CRYPTO_SYMBOLS[crypto]} {CRYPTO_NAMES[crypto]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedBalance && (
                  <p className="text-gray-400 text-sm mt-2 font-mono">Available: {selectedBalance.balance} {transferData.crypto}</p>
                )}
              </div>

              {/* Select Network */}
              <div>
                <Label className="text-gray-300 mb-2 block">Network</Label>
                <Select value={transferData.network} onValueChange={(value) => setTransferData({ ...transferData, network: value })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="transfer-network-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A051E] border-white/10">
                    {NETWORKS[transferData.crypto]?.map(network => (
                      <SelectItem key={network} value={network} className="text-white hover:bg-white/10">
                        {network}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Recipient Address */}
              <div>
                <Label className="text-gray-300 mb-2 block">Recipient Address</Label>
                <Input
                  type="text"
                  placeholder="0x..."
                  value={transferData.to_address}
                  onChange={(e) => setTransferData({ ...transferData, to_address: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 font-mono"
                  required
                  data-testid="transfer-address-input"
                />
              </div>

              {/* Amount */}
              <div>
                <Label className="text-gray-300 mb-2 block">Amount</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="any"
                    placeholder="0.00"
                    value={transferData.amount}
                    onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 pr-16 font-mono"
                    required
                    data-testid="transfer-amount-input"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-mono">
                    {transferData.crypto}
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-400">Network Fee</span>
                  <span className="text-white font-mono">~ 0.0001 {transferData.crypto}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Estimated Time</span>
                  <span className="text-white">2-5 minutes</span>
                </div>
              </div>

              <Button
                type="submit"
                disabled={transferring}
                className="w-full bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full py-6 text-lg font-bold neon-glow-primary"
                data-testid="confirm-transfer-btn"
              >
                {transferring ? 'Processing...' : (
                  <>
                    Send {transferData.crypto} <ArrowRight className="ml-2 inline" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default Transfer;
